import { useEffect, useState } from "react";
import { Loader2, RefreshCw, Check, Pencil } from "lucide-react";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { ugcAPI } from "../../lib/api";

// Try-on step: show one photo of the presenter wearing the exact product; the video starts from it once approved.
export function OutfitPreviewDialog({ open, request, onApprove, onClose }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [note, setNote] = useState("");

  const makePhoto = async (previewId, editNote = "") => {
    setLoading(true);
    setError("");
    try {
      setPreview(await ugcAPI.outfitPreview({ ...request, previewId, editNote }));
      setNote("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't make the outfit photo. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setPreview(null);
      setNote("");
      makePhoto("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-zinc-950 border-white/10 text-white max-w-md" data-testid="outfit-preview-dialog">
        <DialogHeader>
          <DialogTitle>Check the try-on photo</DialogTitle>
          <DialogDescription className="text-white/60">
            Your video starts from this photo. Make sure what she is wearing matches your product.
          </DialogDescription>
        </DialogHeader>
        <div className="w-full max-w-[280px] aspect-[9/16] mx-auto rounded-xl overflow-hidden bg-white/5 flex items-center justify-center">
          {loading ? (
            <div className="flex flex-col items-center gap-2 text-white/60 text-sm" data-testid="outfit-preview-loading">
              <Loader2 className="w-6 h-6 animate-spin" /> Dressing your presenter...
            </div>
          ) : preview ? (
            <img src={preview.image_url} alt="Outfit preview" className="w-full h-full object-cover" data-testid="outfit-preview-image"
              onError={() => setError("The photo didn't load. Tap Try again.")} />
          ) : (
            <p className="text-red-300 text-sm px-6 text-center" data-testid="outfit-preview-error">{error}</p>
          )}
        </div>
        {error && preview && <p className="text-red-300 text-xs" data-testid="outfit-preview-error">{error}</p>}
        {preview && preview.tries_left > 0 && (
          <div className="flex gap-2">
            <Input data-testid="outfit-edit-input" value={note} maxLength={200} disabled={loading}
              onChange={(e) => setNote(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && note.trim() && makePhoto(preview.preview_id, note.trim())}
              placeholder='What should change? e.g. "button the coat"'
              className="bg-white/5 border-white/10 text-white text-sm" />
            <Button data-testid="outfit-edit-apply-btn" disabled={loading || !note.trim()}
              onClick={() => makePhoto(preview.preview_id, note.trim())}
              className="shrink-0 bg-white/10 hover:bg-white/20 text-white">
              <Pencil className="w-4 h-4 mr-2" /> Apply change
            </Button>
          </div>
        )}
        <div className="flex gap-2">
          <Button variant="outline" data-testid="outfit-preview-retry-btn"
            disabled={loading || (preview && preview.tries_left <= 0)}
            onClick={() => makePhoto(preview?.preview_id || "")}
            className="flex-1 min-w-0 h-auto py-2 whitespace-normal border-white/20 bg-transparent text-white hover:bg-white/10">
            <RefreshCw className="w-4 h-4 mr-2 shrink-0" />
            {preview ? `Recreate (${preview.tries_left} left)` : "Try again"}
          </Button>
          <Button data-testid="outfit-preview-approve-btn" disabled={loading || !preview}
            onClick={() => onApprove(preview.preview_id)}
            className="flex-1 min-w-0 h-auto py-2 whitespace-normal bg-pink-500 hover:bg-pink-600 text-white">
            <Check className="w-4 h-4 mr-2 shrink-0" /> Looks right, make video
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
